import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {authApi} from '@/features/auth/services';
import {resetColumnPreferencesCache} from '@/shared/hooks/use-column-preferences';
import {clearAllFilterCustomizations} from '@/shared/hooks/use-session-filter-customization';
import {clearAllCached} from '@/shared/lib/localStorageCache';

// User interface from the centralized API client
interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  metadata?: any;
  /** True when the user is a PM on at least one project (team or creator). */
  hasProjectManagerMembership?: boolean;
}

/** View-as roles available to Admin when switching context */
export type ViewAsRole = 'admin' | 'project_manager' | 'scheduler' | 'moderator';

const VIEW_AS_STORAGE_KEY = 'admin_view_as_role';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** True if user has Admin role */
  isAdmin: boolean;
  /** For Admin: the role to view the UI as. Persisted in localStorage, no re-login needed. */
  viewAsRole: ViewAsRole | null;
  /** Set view-as role (Admin only, persisted) */
  setViewAsRole: (role: ViewAsRole | null) => void;
  /** Effective role for UI: Admin uses viewAsRole when set, else 'admin'. Others use user.role */
  effectiveRole: string | null;
  signUp: (email: string, password: string, name: string, role?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithCentific: (accessToken: string, idToken?: string, refreshToken?: string, expiresIn?: number) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState<ViewAsRole | null>(() => {
    try {
      const stored = localStorage.getItem(VIEW_AS_STORAGE_KEY);
      if (stored && ['admin', 'project_manager', 'scheduler', 'moderator'].includes(stored)) {
        return stored as ViewAsRole;
      }
    } catch {
      // ignore
    }
    return null;
  });

  const isAdmin = !!user && (user.role?.toLowerCase() === 'admin');
  const effectiveRole = isAdmin
    ? (viewAsRole ?? 'project_manager')
    : (user?.role ?? null);

  const setViewAsRole = (role: ViewAsRole | null) => {
    setViewAsRoleState(role);
    try {
      if (role) localStorage.setItem(VIEW_AS_STORAGE_KEY, role);
      else localStorage.removeItem(VIEW_AS_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.me();
      if (response.data) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    
    // Check if user is already logged in
    const token = localStorage.getItem('auth_token');
    if (token) {
      refreshUser();
    } else {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    // Default Admin users to PM view for scoped project access.
    if (isAdmin && !viewAsRole) {
      setViewAsRole('project_manager');
    }
  }, [isAdmin, viewAsRole]);

  const signUp = async (email: string, password: string, name: string, role: string = 'user') => {
    try {
      const response = await authApi.register({ email, password, name, role });
      if (response.data) {
        setUser(response.data.user);
        // Store the auth token if provided
        if (response.data.token) {
          localStorage.setItem('auth_token', response.data.token);
        }
        return { error: null };
      } else {
        return { error: 'No data received from server' };
      }
    } catch (error: any) {
      console.error('Sign up error:', {
        message: error?.message,
        status: error?.response?.status,
      });
      return { error: 'Sign up failed' };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });
      if (response.data) {
        setUser(response.data.user);
        // Store the auth token
        if (response.data.token) {
          localStorage.setItem('auth_token', response.data.token);
        } else {
          console.warn('[AuthContext] No token received from login response');
        }
        return { error: null };
      } else {
        return { error: 'No data received from server' };
      }
    } catch (error: any) {
      console.error('Sign in error:', {
        message: error?.message,
        status: error?.response?.status,
      });
      return { error: 'Sign in failed' };
    }
  };

  const signInWithCentific = async (accessToken: string, idToken?: string, refreshToken?: string, expiresIn?: number) => {
    try {

      const response = await authApi.ssoCallback({
        access_token: accessToken, // Use snake_case to match backend expectation
        id_token: idToken,
        refresh_token: refreshToken,
        expires_in: expiresIn
      });

      if (response.data) {
        setUser(response.data.user);
        // Store the token
        if (response.data.token) {
          localStorage.setItem('auth_token', response.data.token);
        }
        return { error: null };
      } else {
        return { error: 'No data received from server' };
      }
    } catch (error) {
      console.error('Centific SSO error:', error);
      return { error: 'Centific SSO failed' };
    }
  };

  const signOut = async () => {
    try {
      
      // Call backend logout endpoint
      await authApi.logout();
      
      // Clear user state
      setUser(null);
      
      // Clear ALL authentication data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('centific_user');
      localStorage.removeItem(VIEW_AS_STORAGE_KEY);
      // Drop cached per-user column preferences so the next user loads theirs.
      resetColumnPreferencesCache();
      // Drop cached reference-data lists (moderators/session types/projects) —
      // they can be role/project-scoped, so a different user signing in on
      // this browser shouldn't see them until a fresh fetch.
      clearAllCached();

      // Clear submit-data uploads for all projects (session-based)
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('submitDataUploads_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      // Reset session-scoped filter customisations (AC6).
      clearAllFilterCustomizations();

      // Force a page reload to ensure all state is cleared
      setTimeout(() => {
        window.location.href = '/auth';
      }, 100);
      
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Sign out error:', error);
      // Even if there's an error, clear the local state and tokens
      setUser(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('centific_user');
      localStorage.removeItem(VIEW_AS_STORAGE_KEY);
      // Drop cached per-user column preferences so the next user loads theirs.
      resetColumnPreferencesCache();
      // Drop cached reference-data lists (moderators/session types/projects) —
      // they can be role/project-scoped, so a different user signing in on
      // this browser shouldn't see them until a fresh fetch.
      clearAllCached();

      // Clear submit-data uploads for all projects (session-based)
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('submitDataUploads_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      // Reset session-scoped filter customisations (AC6).
      clearAllFilterCustomizations();
      return { error: error instanceof Error ? error.message : 'Sign out failed' };
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAdmin,
    viewAsRole,
    setViewAsRole,
    effectiveRole,
    signUp,
    signIn,
    signInWithCentific,
    signOut,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};